import { Job, SupabaseJob } from '@applicator/shared'
import { useState, useEffect } from 'react'

import { loadRelevanceSettings } from '../lib/relevanceSettings'
import { RelevanceSettings } from '../lib/relevanceSettings'
import { supabase } from '../lib/supabase'
import { useDialogKeyboardShortcuts } from '../hooks/useDialogKeyboardShortcuts'

const TEXT_BLUE_700_SEMIBOLD = 'text-blue-700 font-semibold'

interface ReviewMatchingRulesProps {
  job: Job
  supabaseJob: SupabaseJob
  isOpen: boolean
  onClose: () => void
  onRulesUpdated?: () => void
}

interface ProposedRules {
  scoreThreshold?: number
  prompt?: string
  minDescriptionLength?: number
  maxDescriptionLength?: number
  reasoning?: string
}

function ReviewMatchingRules({ job, supabaseJob, isOpen, onClose, onRulesUpdated }: ReviewMatchingRulesProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentRules, setCurrentRules] = useState<RelevanceSettings | null>(null)
  const [proposedRules, setProposedRules] = useState<ProposedRules | null>(null)
  const [analyzing, setAnalyzing] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadCurrentRules()
    }
  }, [isOpen])

  const loadCurrentRules = async () => {
    try {
      const settings = await loadRelevanceSettings()
      setCurrentRules(settings)
    } catch (err) {
      console.error('Error loading current rules:', err)
      setError('Failed to load current rules')
    }
  }

  // Helper function to build analysis prompt
  const buildAnalysisPrompt = (): string => {
    return `You are analyzing a job posting that was marked as "not relevant" but the user believes it should be relevant.

Current relevance analysis rules:
- Score Threshold: ${currentRules?.scoreThreshold || 75}
- Prompt Template: ${currentRules?.prompt || 'Default'}
- Min Description Length: ${currentRules?.minDescriptionLength || 50}
- Max Description Length: ${currentRules?.maxDescriptionLength || 3000}

Job Details:
- Title: ${job.position}
- Company: ${job.company}
- Location: ${job.location || 'N/A'}
- Description: ${supabaseJob.description.substring(0, 2000)}

Task: Analyze why this job might have been marked as not relevant and propose updated rules that would correctly identify this job as relevant. Consider:
1. Whether the score threshold should be adjusted
2. Whether the prompt template should be improved to better capture relevant aspects
3. Any other rule adjustments needed

Return JSON in this format:
{
  "scoreThreshold": <number or null if no change>,
  "prompt": "<new prompt template or null if no change>",
  "minDescriptionLength": <number or null if no change>,
  "maxDescriptionLength": <number or null if no change>,
  "reasoning": "<explanation of why these changes would help identify this job as relevant>"
}`
  }

  // Helper function to poll for run completion (different API endpoint)
  const pollForRunCompletion = async (
    threadId: string,
    runId: string,
    headers: Record<string, string>,
    maxAttempts = 60
  ): Promise<void> => {
    let attempts = 0
    
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 1000))

      const statusRes = await fetch(`https://api.openai.com/v1/threads/${threadId}/runs/${runId}`, {
        headers: headers
      })

      if (!statusRes.ok) {
        throw new Error(`Failed to check run status: ${statusRes.statusText}`)
      }

      const statusData = await statusRes.json()
      const status = statusData.status

      if (status === 'completed') {
        return
      }
      
      if (status === 'failed' || status === 'cancelled') {
        throw new Error(`Run ${status}: ${statusData.last_error?.message || 'Unknown error'}`)
      }

      attempts++
    }

    throw new Error('Analysis timed out')
  }

  // Helper function to get last message from thread
  const getLastMessageFromThread = async (
    threadId: string,
    headers: Record<string, string>
  ): Promise<string> => {
    const msgRes = await fetch(`https://api.openai.com/v1/threads/${threadId}/messages?order=desc&limit=1`, {
      headers: headers
    })

    if (!msgRes.ok) {
      throw new Error(`Failed to get messages: ${msgRes.statusText}`)
    }

    const messagesData = await msgRes.json()
    return messagesData.data[0].content[0].text.value
  }

  // Helper function to parse proposed rules JSON
  const parseProposedRules = (jsonString: string): ProposedRules => {
    let cleanedJson = jsonString.replace(/```json/g, '').replace(/```/g, '').trim()
    // Use non-greedy match with limit to avoid ReDoS
    const jsonMatch = cleanedJson.match(/\{[^}]*\}/)
    if (jsonMatch) {
      cleanedJson = jsonMatch[0]
    }
    return JSON.parse(cleanedJson)
  }

  const analyzeAndProposeRules = async () => {
    if (!supabaseJob.description) {
      setError('Job description is required for analysis')
      return
    }

    setAnalyzing(true)
    setError(null)

    try {
      const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY
      const ASSISTANT_ID = import.meta.env.VITE_OPENAI_ASSISTANT_ID

      if (!OPENAI_API_KEY || !ASSISTANT_ID) {
        setError('OpenAI API key or Assistant ID not configured')
        return
      }

      const headers = {
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      }

      // Create thread
      const threadRes = await fetch('https://api.openai.com/v1/threads', {
        method: 'POST',
        headers: headers
      })

      if (!threadRes.ok) {
        throw new Error(`Failed to create thread: ${threadRes.statusText}`)
      }

      const threadData = await threadRes.json()
      const threadId = threadData.id

      // Prepare and send prompt
      const analysisPrompt = buildAnalysisPrompt()
      await fetch(`https://api.openai.com/v1/threads/${threadId}/messages`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          role: 'user',
          content: analysisPrompt
        })
      })

      // Run assistant
      const runRes = await fetch(`https://api.openai.com/v1/threads/${threadId}/runs`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          assistant_id: ASSISTANT_ID
        })
      })

      if (!runRes.ok) {
        throw new Error(`Failed to run assistant: ${runRes.statusText}`)
      }

      const runData = await runRes.json()
      const runId = runData.id

      // Poll for completion
      await pollForRunCompletion(threadId, runId, headers)

      // Get and parse result
      const lastMessage = await getLastMessageFromThread(threadId, headers)
      const result = parseProposedRules(lastMessage)
      setProposedRules(result)
    } catch (err) {
      console.error('Error analyzing rules:', err)
      setError(err instanceof Error ? err.message : 'Failed to analyze and propose rules')
    } finally {
      setAnalyzing(false)
    }
  }

  const handleApplyRules = async () => {
    if (!proposedRules) return

    try {
      setLoading(true)
      setError(null)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setError('User not authenticated')
        return
      }

      // Load current settings
      const { data: currentData } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      // Prepare updated settings
      const updatedSettings = {
        user_id: user.id,
        relevance_score_threshold: proposedRules.scoreThreshold !== null && proposedRules.scoreThreshold !== undefined
          ? proposedRules.scoreThreshold
          : currentData?.relevance_score_threshold ?? currentRules?.scoreThreshold ?? 75,
          relevance_prompt: proposedRules.prompt || (currentData?.relevance_prompt ?? currentRules?.prompt),
        relevance_min_description_length: proposedRules.minDescriptionLength !== null && proposedRules.minDescriptionLength !== undefined
          ? proposedRules.minDescriptionLength
          : currentData?.relevance_min_description_length ?? currentRules?.minDescriptionLength ?? 50,
        relevance_max_description_length: proposedRules.maxDescriptionLength !== null && proposedRules.maxDescriptionLength !== undefined
          ? proposedRules.maxDescriptionLength
          : currentData?.relevance_max_description_length ?? currentRules?.maxDescriptionLength ?? 3000,
        updated_at: new Date().toISOString(),
      }

      // Upsert settings
      const { error: upsertError } = await supabase
        .from('user_settings')
        .upsert(updatedSettings, {
          onConflict: 'user_id'
        })

      if (upsertError) {
        throw upsertError
      }

      // Mark job as relevant
      const { error: updateJobError } = await supabase
        .rpc('update_job_relevance', {
          p_user_id: user.id,
          p_job_id: job.id!,
          p_is_relevant: true
        })

      if (updateJobError) {
        console.warn('Failed to update job relevance:', updateJobError)
      }

      if (onRulesUpdated) {
        onRulesUpdated()
      }

      onClose()
    } catch (err) {
      console.error('Error applying rules:', err)
      setError(err instanceof Error ? err.message : 'Failed to apply rules')
    } finally {
      setLoading(false)
    }
  }

  useDialogKeyboardShortcuts({
    isOpen,
    onCancel: onClose,
    onConfirm: proposedRules ? handleApplyRules : undefined,
    confirmDisabled: loading || !proposedRules,
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Review Matching Rules</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Job Information</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Position:</strong> {job.position}</p>
              <p><strong>Company:</strong> {job.company}</p>
              <p><strong>Location:</strong> {job.location || 'N/A'}</p>
            </div>
          </div>

          {currentRules && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Current Rules</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <p><strong>Score Threshold:</strong> {currentRules.scoreThreshold}</p>
                <p><strong>Min Description Length:</strong> {currentRules.minDescriptionLength}</p>
                <p><strong>Max Description Length:</strong> {currentRules.maxDescriptionLength}</p>
                <div>
                  <strong>Prompt Template:</strong>
                  <pre className="mt-1 text-xs bg-white p-2 rounded border overflow-x-auto">
                    {currentRules.prompt}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {!proposedRules && !analyzing && (
            <div className="text-center py-4">
              <button
                onClick={analyzeAndProposeRules}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Analyze and Propose Rules
              </button>
            </div>
          )}

          {analyzing && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Analyzing job and generating rule proposals...</p>
            </div>
          )}

          {proposedRules && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Proposed Rules</h3>
              <div className="bg-blue-50 p-4 rounded-lg space-y-2 text-sm">
                {proposedRules.scoreThreshold !== null && proposedRules.scoreThreshold !== undefined && (
                  <p>
                    <strong>Score Threshold:</strong>{' '}
                    <span className={proposedRules.scoreThreshold !== currentRules?.scoreThreshold ? TEXT_BLUE_700_SEMIBOLD : ''}>
                      {proposedRules.scoreThreshold}
                      {proposedRules.scoreThreshold !== currentRules?.scoreThreshold && ` (was ${currentRules?.scoreThreshold})`}
                    </span>
                  </p>
                )}
                {proposedRules.minDescriptionLength !== null && proposedRules.minDescriptionLength !== undefined && (
                  <p>
                    <strong>Min Description Length:</strong>{' '}
                    <span className={proposedRules.minDescriptionLength !== currentRules?.minDescriptionLength ? 'text-blue-700 font-semibold' : ''}>
                      {proposedRules.minDescriptionLength}
                      {proposedRules.minDescriptionLength !== currentRules?.minDescriptionLength && ` (was ${currentRules?.minDescriptionLength})`}
                    </span>
                  </p>
                )}
                {proposedRules.maxDescriptionLength !== null && proposedRules.maxDescriptionLength !== undefined && (
                  <p>
                    <strong>Max Description Length:</strong>{' '}
                    <span className={proposedRules.maxDescriptionLength !== currentRules?.maxDescriptionLength ? TEXT_BLUE_700_SEMIBOLD : ''}>
                      {proposedRules.maxDescriptionLength}
                      {proposedRules.maxDescriptionLength !== currentRules?.maxDescriptionLength && ` (was ${currentRules?.maxDescriptionLength})`}
                    </span>
                  </p>
                )}
                {proposedRules.prompt && (
                  <div>
                    <strong>Prompt Template:</strong>
                    <pre className="mt-1 text-xs bg-white p-2 rounded border overflow-x-auto">
                      {proposedRules.prompt}
                      {proposedRules.prompt !== currentRules?.prompt && (
                        <div className={`mt-2 ${TEXT_BLUE_700_SEMIBOLD}`}>
                          (Changed from previous template)
                        </div>
                      )}
                    </pre>
                  </div>
                )}
                {proposedRules.reasoning && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <strong>Reasoning:</strong>
                    <p className="mt-1 text-gray-700">{proposedRules.reasoning}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            {proposedRules && (
              <button
                onClick={handleApplyRules}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Applying...' : 'Apply Rules & Mark as Relevant'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReviewMatchingRules
